import type { GoogleTask, GoogleTaskList, GoogleTaskRef } from '../types'

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (options: {
            client_id: string
            scope: string
            callback: (response: { access_token?: string; expires_in?: number; error?: string }) => void
            error_callback?: (error: unknown) => void
          }) => { requestAccessToken: (options?: { prompt?: string }) => void }
          revoke: (token: string, callback?: () => void) => void
        }
      }
    }
  }
}

const TOKEN_KEY = 'thrivecove-google-token'
const EXPIRY_KEY = 'thrivecove-google-expiry'
const SCOPE = 'https://www.googleapis.com/auth/tasks'

function clientId(): string {
  return import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim() ?? ''
}

export function isGoogleConfigured(): boolean {
  return Boolean(clientId())
}

export function isGoogleConnected(): boolean {
  const token = sessionStorage.getItem(TOKEN_KEY)
  const expiry = Number(sessionStorage.getItem(EXPIRY_KEY) ?? 0)
  return Boolean(token && expiry > Date.now() + 30_000)
}

function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity]')
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('Google Identity Services could not load.')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.dataset.googleIdentity = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Google Identity Services could not load.'))
    document.head.appendChild(script)
  })
}

export async function connectGoogleTasks(): Promise<void> {
  if (!isGoogleConfigured()) throw new Error('Add VITE_GOOGLE_CLIENT_ID to enable Google Tasks.')
  await loadGoogleScript()
  await new Promise<void>((resolve, reject) => {
    const tokenClient = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId(),
      scope: SCOPE,
      callback: (response) => {
        if (!response.access_token) {
          reject(new Error(response.error || 'Google authorization was not completed.'))
          return
        }
        sessionStorage.setItem(TOKEN_KEY, response.access_token)
        sessionStorage.setItem(EXPIRY_KEY, String(Date.now() + (response.expires_in ?? 3500) * 1000))
        resolve()
      },
      error_callback: () => reject(new Error('Google authorization was closed.')),
    })
    tokenClient.requestAccessToken({ prompt: 'consent' })
  })
}

export function clearGoogleSession(): void {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(EXPIRY_KEY)
}

export function disconnectGoogleTasks(): void {
  const token = sessionStorage.getItem(TOKEN_KEY)
  if (token && window.google?.accounts) window.google.accounts.oauth2.revoke(token)
  clearGoogleSession()
}

async function googleFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = sessionStorage.getItem(TOKEN_KEY)
  if (!token || !isGoogleConnected()) throw new Error('Google authorization expired. Please reconnect.')
  const response = await fetch(`https://tasks.googleapis.com/tasks/v1${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (response.status === 401) {
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(EXPIRY_KEY)
    throw new Error('Google authorization expired. Please reconnect.')
  }
  if (!response.ok) throw new Error(`Google Tasks request failed (${response.status}).`)
  return response.json() as Promise<T>
}

export async function listTaskLists(): Promise<GoogleTaskList[]> {
  const result = await googleFetch<{ items?: GoogleTaskList[] }>('/users/@me/lists?maxResults=100')
  return result.items ?? []
}

export async function listTasks(listId: string): Promise<GoogleTask[]> {
  const result = await googleFetch<{ items?: GoogleTask[] }>(
    `/lists/${encodeURIComponent(listId)}/tasks?showCompleted=false&showHidden=false&maxResults=100`,
  )
  return (result.items ?? []).filter((task) => task.status !== 'completed')
}

export async function appendSessionToTask(
  task: GoogleTaskRef,
  sessionId: string,
  minutes: number,
  completedAt: number,
): Promise<void> {
  const path = `/lists/${encodeURIComponent(task.listId)}/tasks/${encodeURIComponent(task.taskId)}`
  const current = await googleFetch<GoogleTask>(path)
  const marker = `[ThriveCove:${sessionId}]`
  if ((current.notes ?? '').includes(marker)) return
  const stamp = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(completedAt)
  const entry = `${marker} Focus complete · ${minutes} min · ${stamp}`
  const notes = current.notes?.trim() ? `${current.notes.trim()}\n\n— ThriveCove —\n${entry}` : `— ThriveCove —\n${entry}`
  await googleFetch<GoogleTask>(path, { method: 'PATCH', body: JSON.stringify({ notes }) })
}
